// =============================================================================
// FILE: packages/renderer/src/stores/experienceMode.store.ts
// PURPOSE: R2 Adaptive Experience — the Mode Manager. Three escalating levels:
//   Normal → Dim (🌙, calmer/low-stimulation) → Anxiety Control (🛡️, protective).
//   Dim/Anxiety apply root classes consumed by globals.css; Anxiety also quiets
//   the sound engine. Persisted so the calm environment survives reloads.
// =============================================================================

import { create } from 'zustand';
import { setSoundEnabled, restoreSound } from '../lib/soundManager.js';

export type R2Mode = 'normal' | 'dim' | 'anxiety';
const LS_KEY = 'rcd2.experience.mode';

export function readExperienceMode(): R2Mode {
  try {
    const v = localStorage.getItem(LS_KEY);
    if (v === 'dim' || v === 'anxiety' || v === 'normal') return v;
  } catch { /* */ }
  return 'normal';
}

/** Apply the mode to the DOM + sound engine. Centralised side-effect. */
export function applyExperienceMode(mode: R2Mode): void {
  if (typeof document === 'undefined') return;
  const el = document.documentElement;
  el.classList.toggle('dim-mode', mode === 'dim' || mode === 'anxiety');
  el.classList.toggle('anxiety-mode', mode === 'anxiety');
  try { localStorage.setItem(LS_KEY, mode); } catch { /* */ }
  try { if (mode === 'anxiety') setSoundEnabled(false); else restoreSound(); } catch { /* */ }
}

interface ExperienceState {
  mode: R2Mode;
  panelOpen: boolean;      // Anxiety companion panel visibility
  setMode: (m: R2Mode) => void;
  toggleDim: () => void;
  activateAnxiety: () => void;
  deactivate: () => void;  // back to Normal
  openPanel: () => void;
  closePanel: () => void;
}

export const useExperienceMode = create<ExperienceState>((set, get) => ({
  mode: readExperienceMode(),
  panelOpen: false,

  setMode: (m) => { applyExperienceMode(m); set({ mode: m, panelOpen: m === 'anxiety' }); },
  toggleDim: () => {
    const next: R2Mode = get().mode === 'dim' ? 'normal' : 'dim';
    applyExperienceMode(next);
    set({ mode: next, panelOpen: false });
  },
  activateAnxiety: () => { applyExperienceMode('anxiety'); set({ mode: 'anxiety', panelOpen: true }); },
  deactivate: () => { applyExperienceMode('normal'); set({ mode: 'normal', panelOpen: false }); },
  openPanel: () => set({ panelOpen: true }),
  closePanel: () => set({ panelOpen: false }),
}));
